import CalculationUtil from "../utils/CalculationUtil";
import { datagridColumns } from "../utils/datagridCommon";
import { determineStartingDateMode, isValidDateString } from "../utils/DateUtil";
import { add, division, subtract } from "../utils/Math";
import { getPivotGroupByOptions } from "../utils/misc";
import StateHelper from "../utils/StateHelper";
import { ColumnIds, DatagridColumn, DataType, DateModes, PivotIds, PivotGroupByOption, PublisherOrSiteOption, PublisherRecord, PublisherSiteRecord, SortState } from "../utils/types";
import dayjs from "../utils/dayjs";
import { isDateGroupBy } from "../common/FiltersEventsHelper";
import TagsList from "../components/clinks/tags/TagsList";

const UDSformat = { style: 'currency',currency: 'USD'};
const numberFormat = new Intl.NumberFormat("en-US");

export const DatagridHelper = {
  formatValue : function(type : DataType, value : any | null, precision? : number, showMoreDetails? : boolean | false, showZeros? : boolean | false) : any {
    let newValue : string = null;

    if (value == null || Number.isNaN(value) || value === "" || (value === 0 && !showZeros)){
      return "";
    }
    // format money values as either:
    //   $0.00 - if the value is $0 or so close to 0 that the value doesn't show even rounding to 5 places
    // < $0.01 - if the value is between $0.00000 and $0.01
    //         - the actual value for all other values
    // the tooltip/export (showMoreDetails) has more details to help understand why < $0.01 might be showing
    else if (type === DataType.MONEY) {
      const tooltipValue = value.toLocaleString('en-US', {...UDSformat, minimumFractionDigits: 5 });

      newValue = value.toLocaleString('en-US', UDSformat); 
      if (showMoreDetails) {
        newValue = tooltipValue;
      } else if ( value >= 0 && value < 0.01 && tooltipValue !== "$0.00000" ) {
        newValue = "<$0.01";
      }
    }
    else if (type === DataType.NUMBER) {
      if (typeof precision !== 'undefined') {
        newValue = new Intl.NumberFormat("en-US", { minimumFractionDigits: precision, maximumFractionDigits: precision }).format(value);
      } else {
        newValue = numberFormat.format(value);
      }
    }
    // format percent values as either:
    //  0% - if the value is 0% or so close to 0 that the value doesn't show even rounding to 2 decimal places
    // <0% - if the value is 0% and 1%
    //     - the actual value for all other values
    // the tooltip/export (showMoreDetails) has more details to help understand why < $0.01 might be showing
    else if (type === DataType.PERCENTAGE) {
      const tmp : number = value * 100;

      const displayPrecision = precision ? precision : 0;
      const displayThreshold = (1/(10**displayPrecision));
      const tooltipValue = tmp.toFixed(displayPrecision + 2);
      const displayValue = tmp.toFixed(displayPrecision);

      if (showMoreDetails) {
        newValue = tooltipValue + "%";
      } else if ( tmp >= 0 && tmp < displayThreshold && !showZeros) {
        newValue = "<" + displayThreshold.toFixed(displayPrecision) + "%";
      } else {
        newValue = displayValue + "%";
      }
    }
    else if (type === DataType.MONTH) {
      newValue = value.substring(0,7);
    }
    else if (type === DataType.TAG) {
      return <TagsList tagsList={value.split(",")} containerStyles={{ margin: "0" }} tagStyles={{ margin: "0 4px" }} />;
    }
    else {
      newValue = value.toString();
    }
    return newValue;
  },

  // in the future there may be need for different formatting for some columns,
  // but right now testing shows the datagrid formatting works perfectly when importing
  // the CSV in all major spreadsheet programs
  formatValueForExport : function(column : DatagridColumn , value : any | null) : string {
    // show with more details so numbers are shown as $0.00001 instead of < $0.01
    return this.formatValue(column.type, value, column.precision, true);
  },


  includeFieldInGrid( page : string, fieldName : string){
    const excludeColumn : boolean = page === "connexityPR" && fieldName === ColumnIds.apptapMargin;
    return ! excludeColumn;
  },

  filterColumns: function (responseColumns: any[], datagridColumns : any, page : string): DatagridColumn[] {
    const tableColumns: DatagridColumn[] = [];
    for (let i = 0; i < responseColumns.length; i++) {
      const column = datagridColumns.get(responseColumns[i]);
      if (column) {
        const includeField = this.includeFieldInGrid( page , column.id);
        if ( includeField ){
          tableColumns.push(column);
        }
      }
    }

    // add of calculated columns
    const isRevenueApptapIncluded = tableColumns.find(column => ColumnIds.revenueApptap === column.id);
    const isRevenuePublisherIncluded = tableColumns.find(column => ColumnIds.revenuePublisher === column.id);
    const isConversionsReceivedIncluded = tableColumns.find(column => ColumnIds.conversionsReceived === column.id);
    const isConversionsCreditedIncluded = tableColumns.find(column => ColumnIds.conversionsCredited === column.id);
    const isClicksReceivedIncluded = tableColumns.find(column => ColumnIds.clicksReceived === column.id);
    const isAvgCPAIncluded = tableColumns.find(column => ColumnIds.avgCPA === column.id);
    const isECPCIncluded = tableColumns.find(column => ColumnIds.eCPC === column.id);
    const isTrackedCreditRateIncluded = tableColumns.find(column => ColumnIds.trackedCreditRate === column.id);
    const isPublisherTrackedCreditRateIncluded = tableColumns.find(column => ColumnIds.publisherTrackedCreditRate === column.id);
    const isClicksTrackedIncluded = tableColumns.find(column => ColumnIds.clicksTracked === column.id);
    const isEffCostPerClickIncluded = tableColumns.find(column => ColumnIds.effCostPerClick === column.id);
    const isReportedClickCostIncluded = tableColumns.find(column => ColumnIds.reportedClickCost === column.id);
    const isApptapMarginIncluded = tableColumns.find(column => ColumnIds.apptapMargin === column.id);
    const isApptapNetRevenueIncluded = tableColumns.find(column => ColumnIds.apptapNetRevenue === column.id);
    const isPostConversionEventsIncluded = tableColumns.find(column => ColumnIds.postConversionEvents === column.id);
    const isPostConversionEventRateIncluded = tableColumns.find(column => ColumnIds.postConversionEventRate === column.id);
    const isUntrackedClicksRateIncluded = tableColumns.find(column => ColumnIds.untrackedClicksRate === column.id);
    const isCampaignEventsReceivedIncluded = tableColumns.find(column => ColumnIds.campaignEventsReceived === column.id);
    const isOtherEventsReceivedIncluded = tableColumns.find(column => ColumnIds.otherEventsReceived === column.id);
    const isReturnOnAdvertisingSpendIncluded = tableColumns.find(column => ColumnIds.returnOnAdvertisingSpend === column.id);
    const isPublisherIdIncluded = tableColumns.find(column => ColumnIds.apptapPublisherID === column.id);
    const isPublisherNameIncluded = tableColumns.find(column => ColumnIds.apptapPublisherName === column.id);
    const isOtherEventsRateIncluded = tableColumns.find(column => ColumnIds.otherEventsRate === column.id);

    const isCostOfSaleIncluded = tableColumns.find(column => ColumnIds.costOfSale === column.id);
    const isTargetCostOfSaleIncluded = tableColumns.find(column => ColumnIds.targetCostOfSale === column.id);
    const isEstimatedAdvertiserSalesIncluded = tableColumns.find(column => ColumnIds.estimatedAdvertiserSales === column.id);
    const isTargetAdvertiserSalesIncluded = tableColumns.find(column => ColumnIds.targetAdvertiserSales === column.id);

    if ( !isCostOfSaleIncluded && isRevenueApptapIncluded && isEstimatedAdvertiserSalesIncluded ) {
      tableColumns.push(datagridColumns.get(ColumnIds.costOfSale));
    }
    if ( !isTargetCostOfSaleIncluded && isRevenueApptapIncluded && isTargetAdvertiserSalesIncluded ) {
      tableColumns.push(datagridColumns.get(ColumnIds.targetCostOfSale));
    }
    if (!isAvgCPAIncluded && isRevenueApptapIncluded && isConversionsReceivedIncluded) {
      tableColumns.push(datagridColumns.get(ColumnIds.avgCPA));
    }
    if (!isECPCIncluded && isRevenueApptapIncluded && isClicksReceivedIncluded) {
      tableColumns.push(datagridColumns.get(ColumnIds.eCPC));
    }
    if (!isTrackedCreditRateIncluded && isConversionsReceivedIncluded && isClicksTrackedIncluded) {
      tableColumns.push(datagridColumns.get(ColumnIds.trackedCreditRate));
    }
    if (!isPublisherTrackedCreditRateIncluded && !isConversionsReceivedIncluded && isConversionsCreditedIncluded && isClicksTrackedIncluded) {
      tableColumns.push(datagridColumns.get(ColumnIds.publisherTrackedCreditRate));
    }
    if (!isApptapMarginIncluded && isRevenuePublisherIncluded && isRevenueApptapIncluded) {
      tableColumns.push(datagridColumns.get(ColumnIds.apptapMargin));
    }
    if (!isEffCostPerClickIncluded && (isRevenuePublisherIncluded || isReportedClickCostIncluded) && isClicksReceivedIncluded) {
      tableColumns.push(datagridColumns.get(ColumnIds.effCostPerClick));
    }
    if (!isRevenueApptapIncluded && isRevenuePublisherIncluded && isConversionsCreditedIncluded) {
      // const toIndex = tableColumns.indexOf(datagridColumns.get(ColumnIds.revenuePublisher)) + 1;
      tableColumns.push(datagridColumns.get(ColumnIds.avgPPC));
    }
    if (!isApptapNetRevenueIncluded && isRevenueApptapIncluded && isReportedClickCostIncluded && isRevenuePublisherIncluded) {
      tableColumns.push(datagridColumns.get(ColumnIds.apptapNetRevenue));
    }
    if (!isPostConversionEventRateIncluded && isPostConversionEventsIncluded && isConversionsReceivedIncluded) {
      tableColumns.push(datagridColumns.get(ColumnIds.postConversionEventRate));
    }
    if (!isUntrackedClicksRateIncluded && isClicksReceivedIncluded && isClicksTrackedIncluded) {
      tableColumns.push(datagridColumns.get(ColumnIds.untrackedClicksRate));
    }
    if (!isReturnOnAdvertisingSpendIncluded && isRevenueApptapIncluded && isRevenuePublisherIncluded && isReportedClickCostIncluded) {
      tableColumns.push(datagridColumns.get(ColumnIds.returnOnAdvertisingSpend));
    }
    if (!isOtherEventsReceivedIncluded && isCampaignEventsReceivedIncluded && isConversionsReceivedIncluded) {
      const toIndex = tableColumns.indexOf(datagridColumns.get(ColumnIds.conversionsReceived)) + 1;
      tableColumns.splice(toIndex, 0, datagridColumns.get(ColumnIds.otherEventsReceived));
      if (!isOtherEventsRateIncluded && isClicksTrackedIncluded) {
        const toIndex = tableColumns.indexOf(datagridColumns.get(ColumnIds.otherEventsReceived)) + 1;
        tableColumns.splice(toIndex, 0, datagridColumns.get(ColumnIds.otherEventsRate));
      }
    }
    if (!isPublisherNameIncluded && isPublisherIdIncluded) {
      const toIndex = tableColumns.indexOf(datagridColumns.get(ColumnIds.apptapPublisherID));
      tableColumns.splice(toIndex, 0, datagridColumns.get(ColumnIds.apptapPublisherName));
    }

    // move the reported click cost column to be beside the publisher revenue column
    const idxPubCost = tableColumns.indexOf(datagridColumns.get(ColumnIds.reportedClickCost));
    const idxPubRevenue = tableColumns.indexOf(datagridColumns.get(ColumnIds.revenuePublisher));
    if ( idxPubCost >= 0 && idxPubRevenue >= 0 ) {
      tableColumns.splice(idxPubRevenue + 1, 0, tableColumns.splice(idxPubCost, 1)[0]);
    }

    // move the publisher effective cost per click to right after the Kepler effective gross per click
    const idxGrossPerClick = tableColumns.indexOf(datagridColumns.get(ColumnIds.eCPC));
    const idxEffCostPerClick = tableColumns.indexOf(datagridColumns.get(ColumnIds.effCostPerClick));
    if ( idxGrossPerClick >= 0 && idxEffCostPerClick >= 0 ) {
      tableColumns.splice(idxGrossPerClick + 1, 0, tableColumns.splice(idxEffCostPerClick, 1)[0]);
    }

    return tableColumns;
  },

  calculateColumnValue: function(row: Record<string, number|string>, filteredColumn: DatagridColumn, publishersArray?: PublisherOrSiteOption[]) : string|number {
    if ( filteredColumn.id === ColumnIds.month ) {
      return row[ColumnIds.month] || row[ColumnIds.date];
    } 
    else if( filteredColumn.id === ColumnIds.apptapPublisherName){
      const publisher = publishersArray.find((publisher:any) => publisher.record && publisher.record.apptapPublisherId === row[ColumnIds.apptapPublisherID]);
      const publisherRecord : PublisherRecord = publisher && publisher.record ? publisher.record as PublisherRecord : null;
      if (publisherRecord) {
        if (row[ColumnIds.apptapSiteID]) {
          const publisherSiteRecord : PublisherSiteRecord = publisherRecord.publisherSites.find((publisherSite) => publisherSite.apptapSiteId === row[ColumnIds.apptapSiteID])
          return publisherSiteRecord && publisherSiteRecord.apptapPublisherName ? publisherRecord.apptapPublisherName + " - " + publisherSiteRecord.apptapPublisherSiteName : publisherRecord.apptapPublisherName;
        } else {
          return publisherRecord.apptapPublisherName;
        }
      }
    }
    else if( filteredColumn.id === ColumnIds.avgCPA){
      // Avg. CPA ($) = "Gross Revenue" / "Conversions"
      return division(row[ColumnIds.revenueApptap], row[ColumnIds.conversionsReceived]);
    }
    else if( filteredColumn.id === ColumnIds.eCPC){
      // eCPC (Eff. CPC) ($) = "Gross Revenue" / "Clicks Received"
      return division(row[ColumnIds.revenueApptap], row[ColumnIds.clicksReceived]);
    }
    else if( filteredColumn.id === ColumnIds.trackedCreditRate){
      // Tracked CR (Credit Rate) (%) = ("Conversions Received" / "Tracked Clicks")
      return division(row[ColumnIds.conversionsReceived], row[ColumnIds.clicksTracked]);
    }
    else if( filteredColumn.id === ColumnIds.publisherTrackedCreditRate){
      // Tracked CR (Credit Rate) (%) = ("Conversions Credited" / "Tracked Clicks")
      return division(row[ColumnIds.conversionsCredited], row[ColumnIds.clicksTracked]);
    }
    else if( filteredColumn.id === ColumnIds.apptapMargin){
      return CalculationUtil.apptapMargin(row[ColumnIds.reportedClickCost], row[ColumnIds.revenuePublisher], row[ColumnIds.revenueApptap]);
    }
    else if( filteredColumn.id === ColumnIds.effCostPerClick){
      // Eff Cost Per Click = (Reported Click Cost / Clicks Received)
      return division(add(row[ColumnIds.revenuePublisher], row[ColumnIds.reportedClickCost]), row[ColumnIds.clicksReceived]);
    }
    else if( filteredColumn.id === ColumnIds.apptapNetRevenue){
      // Net Revenue = (Revenue - Reported Click Cost - Revenue Publisher)
      return subtract(subtract(row[ColumnIds.revenueApptap], row[ColumnIds.reportedClickCost]), row[ColumnIds.revenuePublisher]);
    }
    else if( filteredColumn.id === ColumnIds.postConversionEventRate){
      // Post Conversion Event Rate (%) = (Post Conversion Events / onversions Received)
      return division(row[ColumnIds.postConversionEvents], row[ColumnIds.conversionsReceived]);
    }
    else if( filteredColumn.id === ColumnIds.untrackedClicksRate){
      return CalculationUtil.untrackedClicksRate(row[ColumnIds.clicksReceived], row[ColumnIds.clicksTracked]);
    }
    else if( filteredColumn.id === ColumnIds.otherEventsReceived){
      // Other Events Received (%) = Campaign Events Received - Conversions Received
      return subtract(row[ColumnIds.campaignEventsReceived], row[ColumnIds.conversionsReceived]);
    }
    else if( filteredColumn.id === ColumnIds.returnOnAdvertisingSpend){
      // ROAS = Gross Revenue / ( Publisher Credited Payout + Publisher Reported Cost )
      return division(row[ColumnIds.revenueApptap], add(row[ColumnIds.revenuePublisher], row[ColumnIds.reportedClickCost]));
    }
    else if( filteredColumn.id === ColumnIds.otherEventsRate){
      // Other Events Rate (%) = Other Events Received / Tracked Clicks
      return division(row[ColumnIds.otherEventsReceived], add(row[ColumnIds.clicksTracked]));
    }
    // not one of the calculated columns, return the value for the field
    else if ( filteredColumn.id === ColumnIds.costOfSale ) {
      return division(row[ColumnIds.revenueApptap], row[ColumnIds.estimatedAdvertiserSales])
    }
    else if ( filteredColumn.id === ColumnIds.targetCostOfSale ) {
      return division(row[ColumnIds.revenueApptap], row[ColumnIds.targetAdvertiserSales])
    }
    else if ( filteredColumn.id === ColumnIds.avgPPC ) {
      // Avg. Payout Per Conv. = Publisher Credited Payout / Conversions Credited
      return division(row[ColumnIds.revenuePublisher], row[ColumnIds.conversionsCredited])
    }

    // not one of the calculated columns, return the value for the field
    return row[filteredColumn.id];
  },

  calculateSummary: function(rows:Record<string, (number|string)>[], columns:DatagridColumn[], publishersArray?: PublisherOrSiteOption[]) {
    const summaryRow:Record<string,number> = {};
    rows.forEach((row:Record<string, (number|string)> ) => {
      columns.forEach((column) => {
        if (datagridColumns.get(column.id) && datagridColumns.get(column.id).showQuickFilterMenu) return;
        const cellValue = row[column.id];
        if ( cellValue !== null && cellValue !== undefined && typeof(cellValue) !== 'string' && !Array.isArray(cellValue) ) {
          if ( !summaryRow[column.id] ) {
            summaryRow[column.id] = cellValue;
          } else {
            summaryRow[column.id] += cellValue;
          }
        }
      });
    });
    columns.forEach((column) => {
      const cellValue = DatagridHelper.calculateColumnValue(summaryRow, column, publishersArray);
      if ( typeof(cellValue) !== 'string' ) {
        summaryRow[column.id] = cellValue;
      }
    });
    return summaryRow;
  },

  hasNumericValue: function(row:Record<string, (number|string)>, columnKey:string): boolean {
    const cellValue = row[columnKey];
    return cellValue !== null && cellValue !== undefined && typeof(cellValue) !== 'string' && !Array.isArray(cellValue);
  },

  calculateAverage: function (summaryValue:any, row:Record<string, (number|string)>, column:DatagridColumn) {
    if (!column.avgDivideByNumDate) {
      // count number of columns with a numeric value
      const avgDivisor = Object.keys(row).filter(isValidDateString).filter((columnKey) => {
        return this.hasNumericValue(row, columnKey);
      }).length;

      if (typeof(summaryValue) != 'string' && avgDivisor) {
        return summaryValue / avgDivisor;
      }
    }
    return summaryValue;
  },

  sortColumn: function (rows:any[]) {
    const sortState:SortState = StateHelper.getSortState();

    const columnIdToSort = sortState.columnIdSorted;
    let comparator1 = 1;
    let comparator2 = -1;
    if (sortState.sort === 2) {
      comparator1 = -1;
      comparator2 = 1;
    }

    const sortedRows = rows.sort(function (a: any, b: any) {
      let retValue = 0;
      try {

        const valueA : any = a[columnIdToSort];
        const valueB : any = b[columnIdToSort];

        const AHasValue : boolean = valueA != null && valueA !== "";
        const BHasValue : boolean = valueB != null && valueB !== "";

        if( AHasValue && BHasValue){
          if ( valueA > valueB ){
            retValue = comparator2;
          }
          else if ( valueA < valueB ){
            retValue = comparator1;
          }
        }
        else if( AHasValue ){
          retValue = -1;
        }
        else if( BHasValue ){
          retValue = 1;
        }

      }
      catch(err){
        console.log(err);
      }
      return retValue;
    });

    return sortedRows;
  },

  getDatesOrMonthsInRange: function(startDate:dayjs.Dayjs, endDate:dayjs.Dayjs, dateMode:DateModes) {
    const range = [];
    let dayOrMonthIterator = dateMode !== "months" ? endDate : endDate.startOf("month");
    while(!dayOrMonthIterator.isBefore(startDate, "year") && !dayOrMonthIterator.isBefore(startDate, "month") && !dayOrMonthIterator.isBefore(startDate, "day")) {
      switch ( dateMode ) {
        case "days":
          range.push(dayOrMonthIterator.format("YYYY-MM-DD"));
          break;
        case "weeks":
          range.push(dayOrMonthIterator.format("GGGG-[W]WW"));
          break;
        case "months":
          range.push(dayOrMonthIterator.format("YYYY-MM"));
          break;
      }
      dayOrMonthIterator = dateMode !== "months" ? dayjs.utc(dayOrMonthIterator).subtract(1, dateMode) : dayjs.utc(dayOrMonthIterator).subtract(1, dateMode).startOf("month");
    }
    return range;
  },
  
  setSinglePeriodGroupBy: function(currentGroupsBy: string[], dateGroupBy: ColumnIds.date | ColumnIds.week | ColumnIds.month): string[] {
    // remove all existing date period groupbys
    const groupBy = currentGroupsBy.filter((groupBy:string) => !isDateGroupBy(groupBy));

    // add in the desired one
    groupBy.unshift(dateGroupBy);
    return groupBy;
  },

  isPivotByAll: function(pivotBy: string) {
    return pivotBy && pivotBy.split(":").length === 2 && pivotBy.split(":")[0] === "All";
  },

  readPivot: function(pivotBy: string): { pivotFieldSelected: string, pivotGroupBySelected: PivotGroupByOption } {
    let pivotFieldSelected: string = datagridColumns.get(ColumnIds.revenueApptap).id;
    let pivotGroupBySelected: PivotGroupByOption = getPivotGroupByOptions().find((pivot) => pivot.id === PivotIds.off);
    const pivotByParts = pivotBy && pivotBy.split(":").length === 2 ? pivotBy.split(":") : null;
    if (pivotByParts) {
      if (datagridColumns.get(pivotByParts[0]) || pivotByParts[0] === "All") {
        pivotFieldSelected = pivotByParts[0];
      }
      pivotGroupBySelected = getPivotGroupByOptions().find((o) => o.id === pivotByParts[1]);
    }

    return { pivotFieldSelected, pivotGroupBySelected };
  },

  checkPivotCombinationWithGroupsByAndDates: function(currentPivotBy: string, currentGroupBy: string, currentDateStart: dayjs.Dayjs, currentDateEnd: dayjs.Dayjs, isOnPivotSelection: boolean, currentDateMode: DateModes) {
    const currentGroupsBy: string[] = currentGroupBy ? currentGroupBy.split(",") : [];

    let { pivotFieldSelected, pivotGroupBySelected } = this.readPivot(currentPivotBy);

    let newGroupBy:string[] = currentGroupsBy;
    let newDateStart = currentDateStart;
    let newDateEnd = currentDateEnd;
    let newDateMode = currentDateMode;

    if (!isOnPivotSelection && currentPivotBy != null) {
      pivotGroupBySelected = getPivotGroupByOptions().find((pivotGroupByOption) => pivotGroupByOption.id === PivotIds.current);
    }
    const newPivotBy = pivotGroupBySelected.id === PivotIds.off ? null : pivotFieldSelected + ":" + pivotGroupBySelected.id;

    if (newPivotBy !== null) {
      switch(pivotGroupBySelected.id) {
        case PivotIds.current: {
          let groupBy: ColumnIds;
          switch ( currentDateMode ) {
            case "days":
              groupBy = ColumnIds.date;
              break;
            case "weeks":
              groupBy = ColumnIds.week;
              break;
            case "months":
              groupBy = ColumnIds.month;
              break;
          }

          // nothing else changes - the start/end dates and date mode stay the same
          newGroupBy = this.setSinglePeriodGroupBy(currentGroupsBy, groupBy);
          break;
        }
        default: {
          let groupBy: ColumnIds;
          switch ( pivotGroupBySelected.dateMode ) {
            case "days":
              groupBy = ColumnIds.date;
              break;
            case "weeks":
              groupBy = ColumnIds.week;
              break;
            case "months":
              groupBy = ColumnIds.month;
              break;
          }
          newGroupBy = this.setSinglePeriodGroupBy(currentGroupsBy, groupBy);
          newDateStart = pivotGroupBySelected.dateStart;
          newDateEnd = pivotGroupBySelected.dateEnd;
          newDateMode = pivotGroupBySelected.dateMode;
          break;
        }
      }
    } else {
      if ( newDateMode === null ) {
        newDateMode = determineStartingDateMode(currentGroupsBy, currentDateStart, currentDateEnd)
      }

      if ( newDateMode === "months" ) {
        newGroupBy = this.setSinglePeriodGroupBy(currentGroupsBy, ColumnIds.month);
      } else if ( newDateMode === "weeks" ) {
        newGroupBy = this.setSinglePeriodGroupBy(currentGroupsBy, ColumnIds.week);
      } else if ( newDateMode === "days" ) {
          newGroupBy = this.setSinglePeriodGroupBy(currentGroupsBy, ColumnIds.date);
      }
    }

    return { newGroupBy: newGroupBy.join(","), newDateStart, newDateEnd, newPivotBy, newDateMode };
  }

};
